import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import ProjectService from "@app/projects/projectService";
import ZipProjectService from "@app/projects/zipProjectService";
import { NestingParentDropdownItem } from "@app/shared/checkbox-dropdown/types";
import { DropdownItem } from "@app/shared/checkbox-dropdown/types";
import { CheckboxStateEnum } from "@app/shared/qes-standards/forms/Checkbox";
import { ZipCoverageSelectedServiceArea } from "@app/projects/zip-projects/maps/interfaces";
import { ZipPointBadge } from "@app/projects/zip-projects/maps/components/ZipPointBadge";

/** Separator used to encode `{countyRegionSetId}|{zipCode}` in a zip option's key. */
const KEY_SEPARATOR = "|";

/** Builds the composite key for a zip leaf so its county region-set id survives selection. */
export const buildServiceAreaKey = (countyRegionSetId: number, zipCode: string): string =>
  `${countyRegionSetId}${KEY_SEPARATOR}${zipCode}`;

export interface ZipCoverageServiceAreas {
  /** Drill-down tree: state (grandparentTitle) → county (parent) → zip (child). Zip keys are composite. */
  items: NestingParentDropdownItem[];
  /** Resolves a selected zip leaf key back to its zip + county region-set id + label. */
  selectionLookup: Map<string, ZipCoverageSelectedServiceArea>;
  /**
   * False until the fetch answers (and if it fails). An empty tree then means the service area holds no zips
   * rather than "not asked yet" — the difference between clearing a stale selection and wiping a valid one.
   */
  hasData: boolean;
  isLoading: boolean;
}

const EMPTY: ZipCoverageServiceAreas = { hasData: false, isLoading: false, items: [], selectionLookup: new Map() };

/** The county tier a zip is listed under. */
interface CountyTier {
  countyName: string;
  countyRegionSetId: number;
  stateName: string;
}

/**
 * Loads the Service Area as a state → county → zip drill-down that resolves to a single zip.
 * Counties from getCountiesByRunConfigurationId, zips from getServiceAreaZipCodes — both scoped to the
 * run configuration's service area, so the tree can't offer a zip the project never selected.
 * Mirrors FilterContext.getServiceAreas (state → county), plus the added zip level.
 */
export const useZipCoverageServiceAreas = (
  projectId: number | null,
  runConfigurationId: number | null,
): ZipCoverageServiceAreas => {
  const projectService = useMemo(() => new ProjectService(), []);
  const zipProjectService = useMemo(() => new ZipProjectService(), []);

  const { data, isFetching } = useQuery({
    enabled: projectId !== null && runConfigurationId !== null,
    queryFn: async ({ signal }) => {
      // Neither call depends on the other.
      const [states, serviceAreaZips] = await Promise.all([
        projectService.getCountiesByRunConfigurationId(
          String(projectId),
          String(runConfigurationId),
          false,
          true,
          signal,
        ),
        zipProjectService.getServiceAreaZipCodes(projectId!, runConfigurationId!, signal),
      ]);

      const countiesBySSA = new Map<string, CountyTier>(
        states.flatMap((state) =>
          (state.children ?? []).map((county): [string, CountyTier] => [
            county.countySSA,
            { countyName: county.name, countyRegionSetId: Number(county.key), stateName: state.key },
          ]),
        ),
      );

      const selectionLookup = new Map<string, ZipCoverageSelectedServiceArea>();
      const childrenByCountySSA = new Map<string, DropdownItem<string>[]>();

      for (const zip of serviceAreaZips) {
        // Adequacy counts a zip under its primary county only, so a zip whose primary county isn't in
        // the service area has no tier to sit under.
        const county = countiesBySSA.get(zip.primaryCountySSA);
        if (!county) {
          continue;
        }

        const key = buildServiceAreaKey(county.countyRegionSetId, zip.zipCode);
        const label = zip.name ? `${zip.zipCode} - ${zip.name}` : zip.zipCode;
        // countyName/stateName feed the details-bar callout; the applied selection otherwise loses them.
        selectionLookup.set(key, {
          countyName: county.countyName,
          countyRegionSetId: county.countyRegionSetId,
          label,
          stateName: county.stateName,
          zipCode: zip.zipCode,
        });

        const children = childrenByCountySSA.get(zip.primaryCountySSA) ?? [];
        if (!zip.isZipPoint) {
          children.push({ key, name: label });
        } else {
          // name stays the plain string: search, sort and the selected-input text all key off it.
          children.push({
            display: (
              <span className="zip-coverage-map__service-area-option">
                <span className="zip-coverage-map__service-area-option-label">{label}</span>
                <ZipPointBadge testId={`zip-coverage-map__zip-point-badge--${zip.zipCode}`} />
              </span>
            ),
            key,
            name: label,
          });
        }
        childrenByCountySSA.set(zip.primaryCountySSA, children);
      }

      // state (grandparentTitle) → county (parent) → zip (child).
      const items: NestingParentDropdownItem[] = [];
      childrenByCountySSA.forEach((children, countySSA) => {
        const county = countiesBySSA.get(countySSA)!;
        items.push({
          checkboxStatus: CheckboxStateEnum.Unchecked,
          children: children.sort((a, b) => a.name.localeCompare(b.name)),
          grandparentTitle: county.stateName,
          key: String(county.countyRegionSetId),
          name: county.countyName,
        });
      });

      // Sort by state then county so the grandparent groups are ordered.
      items.sort((a, b) =>
        `${a.grandparentTitle}${a.name}`.localeCompare(`${b.grandparentTitle}${b.name}`),
      );

      return { items, selectionLookup };
    },
    queryKey: ["zip-coverage-map", "service-areas", projectId, runConfigurationId],
    staleTime: 5 * 60 * 1000,
  });

  if (!data) {
    return { ...EMPTY, isLoading: isFetching };
  }
  return { hasData: true, isLoading: isFetching, items: data.items, selectionLookup: data.selectionLookup };
};
